import type { Lang } from '../i18n';

/**
 * A flag per language, drawn inline.
 *
 * <p>Not emoji: Windows has no glyphs for the regional indicator pairs, so a flag emoji renders
 * there as the two letters "GB" or "PL" - on the very machines much of the audience uses. Inline
 * SVG draws the same on every platform and scales with the type around it.
 *
 * <p>These are icons at sixteen pixels, not reproductions: the Union Jack's diagonals are centred
 * rather than offset, and Portugal's armillary sphere and Spain's arms are a ring and a bar. They
 * are drawn to be recognised at a glance, and they are never the only thing identifying a
 * language - the name or the code is always beside them.
 */
export default function Flag({ lang, size = 16 }: { lang: Lang; size?: number }) {
  return (
    <span className="flag" style={{ width: size * 1.4, height: size }} aria-hidden="true">
      {FLAGS[lang]}
    </span>
  );
}

const FLAGS: Record<Lang, JSX.Element> = {
  en: (
    <svg viewBox="0 0 60 40" preserveAspectRatio="none">
      <rect width="60" height="40" fill="#012169" />
      <path d="M0 0 60 40M60 0 0 40" stroke="#fff" strokeWidth="9" />
      <path d="M0 0 60 40M60 0 0 40" stroke="#C8102E" strokeWidth="4" />
      <path d="M30 0V40M0 20H60" stroke="#fff" strokeWidth="14" />
      <path d="M30 0V40M0 20H60" stroke="#C8102E" strokeWidth="8" />
    </svg>
  ),
  tr: (
    <svg viewBox="0 0 60 40" preserveAspectRatio="none">
      <rect width="60" height="40" fill="#E30A17" />
      <circle cx="24" cy="20" r="9" fill="#fff" />
      <circle cx="27.5" cy="20" r="7.2" fill="#E30A17" />
      <path d="m37.5 20 5.6-1.9-3.5 4.8v-5.9l3.5 4.8z" fill="#fff" />
    </svg>
  ),
  pl: (
    <svg viewBox="0 0 60 40" preserveAspectRatio="none">
      <rect width="60" height="20" fill="#fff" />
      <rect y="20" width="60" height="20" fill="#DC143C" />
    </svg>
  ),
  pt: (
    <svg viewBox="0 0 60 40" preserveAspectRatio="none">
      <rect width="60" height="40" fill="#FF0000" />
      <rect width="24" height="40" fill="#006600" />
      <circle cx="24" cy="20" r="7.5" fill="#FFE900" stroke="#fff" strokeWidth="1.2" />
      <circle cx="24" cy="20" r="4" fill="#fff" stroke="#D52B1E" strokeWidth="1.6" />
    </svg>
  ),
  es: (
    <svg viewBox="0 0 60 40" preserveAspectRatio="none">
      <rect width="60" height="40" fill="#AA151B" />
      <rect y="10" width="60" height="20" fill="#F1BF00" />
      <rect x="12" y="16" width="7" height="9" rx="1" fill="#AA151B" />
    </svg>
  ),
};
